import { useEffect, useRef, useState } from 'react';

import { useRequisicaoMaisRecente } from './useRequisicaoMaisRecente';

export const ATRASO_DA_BUSCA = 300;
export const MINIMO_PARA_BUSCAR = 2;

/**
 * Busca que acontece enquanto se digita (AJ2-38 a AJ2-45, AJ3-33 a AJ3-37): a partir de 2 letras, 300 ms depois da
 * última tecla, chama `onBuscar` com o texto aparado. `buscarAgora` (Enter ou botão) busca na hora, com qualquer
 * texto, e devolve se buscou. Esvaziar o campo chama `onBuscar('')` na hora. O mesmo termo não é buscado duas
 * vezes seguidas.
 *
 * `onBuscar` recebe também `ehAtual()`: falso quando outra busca começou depois desta (ou o campo foi limpo), pela
 * mesma regra de `useRequisicaoMaisRecente`. Quem busca de forma assíncrona confere antes de mostrar o resultado, para
 * a resposta antiga não cobrir a nova. Esta é a única fonte da regra "2 letras, 300 ms": `BuscaDePessoas` usa este hook.
 */
export function useBuscaAoDigitar(onBuscar: (termo: string, ehAtual: () => boolean) => void, valorInicial = '') {
  const [texto, setTexto] = useState(valorInicial);
  const agendada = useRef<ReturnType<typeof setTimeout>>();
  const ultimoBuscado = useRef(valorInicial.trim());
  const { iniciar, descartar } = useRequisicaoMaisRecente();
  const onBuscarRef = useRef(onBuscar);
  onBuscarRef.current = onBuscar;

  useEffect(() => () => clearTimeout(agendada.current), []);

  function buscarSeMudou(termo: string) {
    if (termo === ultimoBuscado.current) return false;
    ultimoBuscado.current = termo;
    onBuscarRef.current(termo, iniciar());
    return true;
  }

  function digitar(novo: string) {
    setTexto(novo);
    clearTimeout(agendada.current);

    const termo = novo.trim();
    if (!termo) {
      buscarSeMudou('');
      return;
    }
    if (termo.length < MINIMO_PARA_BUSCAR) return;

    agendada.current = setTimeout(() => buscarSeMudou(termo), ATRASO_DA_BUSCA);
  }

  function buscarAgora() {
    clearTimeout(agendada.current);
    return buscarSeMudou(texto.trim());
  }

  /** Limpa o campo sem buscar (a pessoa escolheu um resultado). A próxima busca, mesmo do termo anterior, acontece. */
  function limpar() {
    clearTimeout(agendada.current);
    setTexto('');
    ultimoBuscado.current = '';
    descartar();
  }

  return { texto, digitar, buscarAgora, limpar };
}
